/**
 * CustomerSelect Component - T105
 *
 * Searchable dropdown for selecting customers
 */
import { useState, useRef, useEffect, useId, type KeyboardEvent } from 'react';
import { useCustomerSearch, type Customer } from '../../hooks/useCustomers';
import { LoadingSpinner } from '../ui/LoadingSpinner';

interface CustomerSelectProps {
  value: string | null;
  onChange: (customerId: string, customer: Customer | null) => void;
  selectedCustomer?: Customer | null;
  error?: string;
  disabled?: boolean;
}

export function CustomerSelect({
  value,
  onChange,
  selectedCustomer,
  error,
  disabled,
}: CustomerSelectProps) {
  const resultListId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedButtonRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLUListElement>(null);

  const { data: customers, isLoading, isError } = useCustomerSearch(search, 10);
  const results = customers ?? [];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) inputRef.current?.focus();
    else if (wasOpen.current) selectedButtonRef.current?.focus();
    wasOpen.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;
    resultsRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex, isOpen]);

  const handleSelect = (customer: Customer) => {
    onChange(customer.id, customer);
    setSearch('');
    setActiveIndex(-1);
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange('', null);
    setActiveIndex(-1);
    setSearch('');
    setIsOpen(true);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      return;
    }
    if (event.key === 'ArrowDown' && isOpen && results.length) {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % results.length);
    } else if (event.key === 'ArrowUp' && isOpen && results.length) {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + results.length) % results.length);
    } else if (event.key === 'Enter' && isOpen) {
      event.preventDefault();
      if (!isLoading && !isError && search.length >= 2 && results[activeIndex]) handleSelect(results[activeIndex]);
    }
  };

  const displayValue = selectedCustomer
    ? `${selectedCustomer.customerNumber} - ${selectedCustomer.companyName}`
    : '';

  return (
    <div className="w-full" ref={containerRef}>
      <label className="block text-sm font-medium text-white/70 mb-1.5">
        Kunde *
      </label>
      <div className="relative">
        {/* Selected customer display / search input */}
        {value && selectedCustomer && !isOpen ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              ref={selectedButtonRef}
              aria-label={`Kunde ${displayValue} ändern`}
              onClick={() => !disabled && setIsOpen(true)}
              disabled={disabled}
              className={`
              w-full px-4 py-2.5 rounded-lg flex items-center justify-between
              bg-white/[0.03] border border-white/[0.08]
              text-white cursor-pointer
              hover:border-white/20 hover:bg-white/[0.05]
              transition-colors duration-200
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
              ${error ? 'border-red-500/50' : ''}
            `}
            >
              <span>{displayValue}</span>
            </button>
            {!disabled && (
              <button
                type="button"
                aria-label="Kunde entfernen"
                onClick={handleClear}
                className="shrink-0 rounded px-2 py-2 text-white/40 hover:text-white/70"
              >
                <svg className="w-4 h-4" aria-hidden="true" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        ) : (
          <input
            ref={inputRef}
            role="combobox"
            aria-label="Kunde"
            aria-expanded={isOpen}
            aria-controls={resultListId}
            aria-activedescendant={isOpen && activeIndex >= 0 && results[activeIndex] ? `${resultListId}-${results[activeIndex].id}` : undefined}
            aria-autocomplete="list"
            type="text"
            placeholder="Kunde suchen..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setActiveIndex(-1);
              if (!isOpen) setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className={`
              w-full px-4 py-2.5 rounded-lg
              bg-white/[0.03] border border-white/[0.08]
              text-white placeholder:text-white/30
              focus:outline-none focus:border-white/20 focus:bg-white/[0.05]
              transition-colors duration-200
              disabled:opacity-50 disabled:cursor-not-allowed
              ${error ? 'border-red-500/50 focus:border-red-500/50' : ''}
            `}
          />
        )}

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-1 rounded-lg bg-zinc-900 border border-white/10 shadow-xl overflow-hidden">
            {search.length < 2 ? (
              <div className="px-4 py-3 text-white/40 text-sm">
                Mindestens 2 Zeichen eingeben...
              </div>
            ) : isLoading ? (
              <div className="px-4 py-3 flex items-center gap-2">
                <LoadingSpinner size="sm" />
                <span className="text-white/50">Suche...</span>
              </div>
            ) : isError ? (
              <div role="status" className="px-4 py-3 text-red-300 text-sm">
                Kundensuche fehlgeschlagen. Bitte erneut versuchen.
              </div>
            ) : results.length > 0 ? (
              <ul ref={resultsRef} id={resultListId} role="listbox" className="max-h-60 overflow-auto">
                {results.map((customer, index) => (
                    <button
                      key={customer.id}
                      type="button"
                      role="option"
                      id={`${resultListId}-${customer.id}`}
                      aria-selected={index === activeIndex}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => handleSelect(customer)}
                      className={`w-full px-4 py-2.5 text-left hover:bg-white/5 transition-colors ${index === activeIndex ? 'bg-white/5' : ''}`}
                    >
                      <div className="text-white font-medium">
                        {customer.companyName}
                      </div>
                      <div className="text-sm text-white/50">
                        {customer.customerNumber}
                        {customer.email && ` • ${customer.email}`}
                      </div>
                    </button>
                ))}
              </ul>
            ) : (
              <div className="px-4 py-3 text-white/40 text-sm">
                Keine Kunden gefunden
              </div>
            )}
          </div>
        )}
      </div>
      {error && <p className="mt-1.5 text-sm text-red-400">{error}</p>}
    </div>
  );
}
